/* eslint-disable camelcase -- GA4 wire parameter names. */
import { CaptureWindow, toGa4Event } from "@/utils/Ga4Capture";

export function installGa4Capture(
  target: CaptureWindow,
  measurementId: string,
  isEnabled: () => boolean,
  pageLocation: () => string
): void {
  // Keep migration receipts out of the existing production properties and retired pilot.
  if (!/^G-[A-Z0-9]+$/.test(measurementId) ||
      ["G-M22M74QTC9", "G-2GXSG24TGT", "G-FMQDTGMCBS", "G-8S05ZQ699L"].includes(measurementId) ||
      target.torreGa4Capture || !isEnabled()) {
    target.torreGa4Pending = [];
    if (!target.torreGa4Capture) { target.torreGa4Capture = () => {}; }
    return;
  }

  target.dataLayer = target.dataLayer || [];
  if (!target.gtag) {
    target.gtag = function() { target.dataLayer!.push(arguments); };
    target.gtag("js", new Date());
  }
  const gtag = target.gtag;
  const seen = new Set<string>();
  let failureReported = false;
  const pageParameters = () => ({ page_location: pageLocation(), page_title: "Torre", page_referrer: "" });

  gtag("config", measurementId, {
    ...pageParameters(), send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false
  });
  target.torreGa4Capture = (payload) => {
    try {
      if (!isEnabled()) { return; }
      const event = toGa4Event(payload);

      if (!event || seen.has(String(payload.eid))) { return; }
      gtag("event", event.name, { ...pageParameters(), ...event.params, send_to: measurementId });
      seen.add(String(payload.eid));
      if (seen.size > 2000) { seen.delete(seen.values().next().value!); }
    } catch {
      if (!failureReported) {
        failureReported = true;
        console.warn("GA4 parallel capture unavailable");
      }
    }
  };
  const pending = target.torreGa4Pending || [];

  target.torreGa4Pending = [];
  pending.forEach(payload => target.torreGa4Capture!(payload));
  if (!target.document.querySelector("script[src*=\"googletagmanager.com/gtag/js\"]")) {
    const script = target.document.createElement("script");

    script.async = true;
    script.referrerPolicy = "no-referrer";
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    target.document.head.appendChild(script);
  }
}

export function Ga4CapturePlugin() {
  return {
    afterTrack(payload: Record<string, unknown>): void {
      const target = window as CaptureWindow;

      try {
        if (target.torreGa4Capture) { target.torreGa4Capture(payload); } else {
          target.torreGa4Pending = target.torreGa4Pending || [];
          const event = toGa4Event(payload);

          if (event && target.torreGa4Pending.length < 500) {
            const params = event.params;

            target.torreGa4Pending.push({
              eid: params.source_event_id,
              dtm: params.source_timestamp_ms,
              e: params.source_event_type,
              aid: params.app_id,
              se_ca: params.se_category,
              se_ac: params.se_action,
              se_va: params.se_value,
              ue_pr: params.source_event_name
                ? JSON.stringify({
                  data: {
                    schema:
                `iglu:${params.source_event_vendor}/${params.source_event_name}/jsonschema/${params.source_event_version}`
                  }
                })
                : undefined
            });
          }
        }
      } catch {
      // A failing mirror must leave the existing tracker untouched.
      }
    }
  };
}
